




















































































































































































import { library } from '@fortawesome/fontawesome-svg-core'
import { faCopy } from '@fortawesome/free-solid-svg-icons'

library.add(faCopy)

import {
  defineComponent,
  ref,
  inject,
  watch,
  onMounted,
  computed
} from '@vue/composition-api'
import { useRouter } from '@u3u/vue-hooks'
import {
  validateNewLogin,
  ClientAdminApiKey,
  validateUpdatedLogin
} from '@/rest/client-admin-api'
import KaiFormInput from '@/contexts/frontend/KaiFormInput.vue'
import KaiFormSelect from '@/contexts/frontend/KaiFormSelect.vue'
import KaiMessage from '@/contexts/frontend/KaiMessage.vue'
import RoleSelector from '@/contexts/client-admin/roles/RoleSelector.vue'
import {
  handleApiResponse,
  ServerError,
  ValidationError
} from '@/rest/responses'

const clientToSelectOption = (client: any) => ({
  value: client.id,
  display: client.name
})

const userToSelectOption = (user: any) => ({
  value: user.id,
  display: user.name
})

export default defineComponent({
  components: {
    KaiFormInput,
    KaiFormSelect,
    KaiMessage,
    RoleSelector
  },

  props: {
    loginId: String
  },

  setup(props) {
    const name = ref('')
    const email = ref('')
    const messages = ref([])
    const messageType = ref('none')
    const fieldErrors = ref({})
    const { router } = useRouter()
    const clientAdminApi = inject(ClientAdminApiKey)
    const clients = ref([])
    const clientAccessSelection = ref<Array<string>>([])
    const accessibleClients = ref([])
    const teamAccessSelection = ref<Array<string>>([])
    const accessibleTeams = ref([])
    const selectedClient = ref('')
    const userSelection = ref('')
    const users = ref([])
    const isSaved = ref(false)
    const isDisabled = ref(false)
    const loginId = ref('')
    const rolesSelected = ref<Array<string>>([])

    const validationError = (error: ValidationError) => {
      messages.value = error.formErrors
      messageType.value = 'danger'
    }

    const serverError = (error: ServerError) => {
      messages.value = [error.message]
      messageType.value = 'danger'
    }

    onMounted(async () => {
      await clientAdminApi
        .ListClients()
        .then(results => results.map(clientToSelectOption))
        .then(results => (clients.value = results))

      await clientAdminApi
        .ListUsers()
        .then(results => results.map(userToSelectOption))
        .then(results => (users.value = results))

      if (props.loginId != undefined) {
        await clientAdminApi.GetLogin(props.loginId).then(
          handleApiResponse({
            success: login => {
              loginId.value = login.id
              name.value = login.name
              email.value = login.email
              clientAccessSelection.value =
                login.clientAccess.length == 0
                  ? [login.client]
                  : login.clientAccess
              selectedClient.value = login.client
              userSelection.value = login.user
              teamAccessSelection.value = login.teamAccess ?? []
              rolesSelected.value = login.roles
              isSaved.value = true
              isDisabled.value = !login.enabled
            },
            validationError,
            serverError
          })
        )
      }
    })

    watch(
      [clientAccessSelection],
      () =>
        (accessibleClients.value = clients.value.filter(client =>
          clientAccessSelection.value.includes(client.value)
        ))
    )

    watch([selectedClient], async () => {
      const teams = await clientAdminApi.ListTeams({
        clientId: selectedClient.value
      })
      accessibleTeams.value = teams
        .filter(team => team.client === selectedClient.value)
        .map(clientToSelectOption)
    })

    const loginIdFormatted = computed(() =>
      isSaved.value ? loginId.value : '(none)'
    )
    const copyLoginId = () => navigator.clipboard.writeText(loginId.value)

    const onRoleSelected = async (role: string) =>
      await clientAdminApi.AddRole(loginId.value, role)

    const onRoleDeselected = async (role: string) =>
      await clientAdminApi.RemoveRole(loginId.value, role)

    const resetPassword = () =>
      clientAdminApi.ResetPassword(loginId.value).then(
        handleApiResponse({
          success: () => {
            messages.value = ['Password reset']
            messageType.value = 'success'
          },
          validationError,
          serverError
        })
      )

    const sendWelcome = () =>
      clientAdminApi.SendWelcome(email.value).then(
        handleApiResponse({
          success: () => {
            messages.value = ['Welcome email sent']
            messageType.value = 'success'
          },
          validationError,
          serverError
        })
      )

    const disableLogin = () =>
      clientAdminApi.DisableLogin(email.value).then(
        handleApiResponse({
          success: () => {
            messages.value = ['Login disabled']
            messageType.value = 'success'
          },
          validationError,
          serverError
        })
      )

    const enableLogin = () =>
      clientAdminApi.EnableLogin(email.value).then(
        handleApiResponse({
          success: () => {
            messages.value = ['Login enabled']
            messageType.value = 'success'
          },
          validationError,
          serverError
        })
      )

    const back = () => router.go(-1)

    const save = async () => {
      if (isSaved.value == true) {
        const validated = validateUpdatedLogin({
          name: name.value,
          user: userSelection.value,
          clientAccess: clientAccessSelection.value,
          client: selectedClient.value,
          teamAccess: teamAccessSelection.value
        })
        if (validated._tag == 'success') {
          const apiResponse = await clientAdminApi.UpdateLogin(
            loginId.value,
            validated.data
          )
          if (apiResponse._tag == 'success') {
            messages.value = ['Login saved']
            messageType.value = 'success'
          } else if (apiResponse.error._tag == 'validation') {
            messages.value = apiResponse.error.formErrors
            messageType.value = 'danger'
          } else if (apiResponse.error._tag == 'server') {
            messages.value = [apiResponse.error.message]
            messageType.value = 'danger'
          }
        } else {
          fieldErrors.value = validated.error.fieldErrors
        }
      } else {
        const validated = validateNewLogin({
          name: name.value,
          email: email.value.toLowerCase(),
          user: userSelection.value,
          client: selectedClient.value,
          clientAccess: clientAccessSelection.value,
          teamAccess: teamAccessSelection.value
        })
        if (validated._tag == 'success') {
          const apiResponse = await clientAdminApi.CreateLogin(validated.data)
          if (apiResponse._tag == 'success') {
            messages.value = ['Login created']
            messageType.value = 'success'
            loginId.value = apiResponse.data.id
            isSaved.value = true
          } else if (apiResponse.error._tag == 'validation') {
            messages.value = apiResponse.error.formErrors
            messageType.value = 'danger'
          } else if (apiResponse.error._tag == 'server') {
            messages.value = [apiResponse.error.message]
            messageType.value = 'danger'
          }
        } else {
          fieldErrors.value = validated.error.fieldErrors
        }
      }
    }

    return {
      sendWelcome,
      resetPassword,
      isDisabled,
      disableLogin,
      enableLogin,
      back,
      save,
      fieldErrors,
      copyLoginId,
      loginIdFormatted,
      name,
      email,
      clients,
      clientAccessSelection,
      teamAccessSelection,
      onRoleSelected,
      onRoleDeselected,
      accessibleClients,
      accessibleTeams,
      selectedClient,
      users,
      userSelection,
      rolesSelected,
      messages,
      messageType,
      isSaved
    }
  }
})
